import { useContext, useEffect } from 'react';
import { QueryClient, QueryClientContext, useQuery } from '@tanstack/react-query';
import apiClient from '../../api/client';
import { API } from '../../api/endpoints';
import { useGlobalStore } from '../../stores/globalStore';

/**
 * Reports that are ready and not yet opened: the count on the sidebar's
 * Print Queue item (moved from Reports, Jeff 2026-10-23; first asked for 2026-10-06: "should show you the completed reports
 * with 7 since there are 7 completed reports").
 *
 * The rule, the same on both sides (API: ReportDAL.ReadyReportCountSql):
 * the signed-in user's own reports whose status is still Completed, not
 * deleted and not a hidden ticket. That is exactly the set of rows showing a
 * "Completed" pill on the Reports page with its default view.
 *
 * A report leaves the count when its pill leaves Completed. Opening it on
 * screen (the viewer, or its PDF in a tab) marks it Viewed (POST MarkViewed:
 * status 8 with no Last Printed date, so the desktop shows Printed); printing
 * it marks it Printed (status 8 with the date, POST MarkPrinted), as WinForms
 * does (UpdateLastPrintedDateTime). Deleting it removes it too. The
 * "is ready" toast's View and the auto-opener mark it the same way.
 */

/** Status values used here (types/reports REPORT_STATUS). */
const COMPLETED = 2;
const PRINTED = 8;

/** How often the sidebar asks for the count. One COUNT(*) a minute. */
export const READY_REPORT_POLL_MS = 60_000;

export const readyReportsKey = (facilityId: number, userId: number) =>
  ['reports', 'readyCount', facilityId, userId] as const;

interface ReadyCandidate {
  fKey_User: number;
  status: number;
  dateTimeDeleted: string | null;
  category: number;
}

/** The ready count for `userId`, from a list the Reports page already fetched. */
export function countReadyReports(docs: readonly ReadyCandidate[], userId: number): number {
  return docs.filter((d) =>
    d.fKey_User === userId
    && d.status === COMPLETED
    && d.dateTimeDeleted == null
    && (d.category ?? 0) === 0,
  ).length;
}

/** Spoken name for the sidebar item: "Reports, 7 ready". */
export function readyReportsLabel(label: string, count: number): string {
  return count > 0 ? `${label}, ${count} ready` : label;
}

/** Badge text: the count, capped so it stays one small pill. */
export function readyReportsBadgeText(count: number): string {
  return count > 99 ? '99+' : String(count);
}

/**
 * Something changed the user's ready reports. With `count`, the caller
 * already knows the new number (the Reports page just loaded the list);
 * without it the badge asks the API again.
 *
 * A window event rather than a query-client call so the auto-opener (an
 * app-root provider) and the toasts can tell the badge without needing a
 * query client of their own.
 */
const CHANGED_EVENT = 'dietech:ready-reports-changed';

export function announceReadyReports(facilityId: number, userId: number, count?: number) {
  try {
    window.dispatchEvent(new CustomEvent(CHANGED_EVENT, { detail: { facilityId, userId, count } }));
  } catch { /* no window (SSR / worker) */ }
}

/**
 * A report the user just opened or printed. A tester, 11-03: "The viewed report label changes one
 * report late": the Print Queue kept a report Completed until its next poll, so its Viewed pill
 * showed up only when the next report was opened. The change is announced twice: the moment it
 * happens (`saved: false`), so the row shows it at once, and once the API has saved it
 * (`saved: true`), so the list is read again to confirm.
 */
export interface ReportStatusChange {
  facilityId: number;
  userId: number;
  reportDocumentId: number;
  to: 'viewed' | 'printed';
  saved: boolean;
}

const STATUS_EVENT = 'dietech:report-status-changed';

function announceStatus(change: ReportStatusChange) {
  try {
    window.dispatchEvent(new CustomEvent(STATUS_EVENT, { detail: change }));
  } catch { /* no window (SSR / worker) */ }
}

/** Be told when the user opens or prints a report. Returns the unsubscribe. */
export function onReportStatusChange(handler: (change: ReportStatusChange) => void): () => void {
  const listener = (e: Event) => {
    const change = (e as CustomEvent<ReportStatusChange>).detail;
    if (change) handler(change);
  };
  window.addEventListener(STATUS_EVENT, listener);
  return () => window.removeEventListener(STATUS_EVENT, listener);
}

interface StatusRow {
  pKey_RS_ReportDocument: number;
  status: number;
  lastPrintedDateTime?: string | null;
}

/**
 * The rows as they show once the change lands, as the API saves it: opened, a Completed report
 * becomes Viewed (Printed, status 8, with no Last Printed date) and a Printed one stays Printed;
 * printed, a Completed or Viewed report becomes Printed with this moment as its Last Printed.
 * The same array when nothing changes.
 */
export function applyReportStatusChange<T extends StatusRow>(rows: T[], change: Pick<ReportStatusChange, 'reportDocumentId' | 'to'>, now: Date = new Date()): T[] {
  let changed = false;
  const next = rows.map((row) => {
    if (row.pKey_RS_ReportDocument !== change.reportDocumentId) return row;
    if (change.to === 'viewed') {
      if (row.status !== COMPLETED) return row;
      changed = true;
      return { ...row, status: PRINTED, lastPrintedDateTime: null };
    }
    if (row.status !== COMPLETED && row.status !== PRINTED) return row;
    changed = true;
    return { ...row, status: PRINTED, lastPrintedDateTime: now.toISOString() };
  });
  return changed ? next : rows;
}

async function markReport(url: string, change: ReportStatusChange, query?: { userId: number }) {
  announceStatus(change);
  try {
    await apiClient.post(url, null, query ? { params: query } : undefined);
  } catch {
    // Not worth an error toast: the report opened or printed, only its pill
    // and the badge are stale, and the next poll or the Reports page puts it right.
  }
  announceStatus({ ...change, saved: true });
  announceReadyReports(change.facilityId, change.userId);
}

/**
 * Mark a report Viewed: it was opened on screen. The API moves only Completed
 * reports, so viewing a Printed one leaves it Printed.
 */
export async function markReportViewed(facilityId: number, reportDocumentId: number, userId: number) {
  if (!facilityId || !reportDocumentId) return;
  await markReport(API.reportMarkViewed(facilityId, reportDocumentId), { facilityId, userId, reportDocumentId, to: 'viewed', saved: false });
}

/** Mark a report Printed: the user printed it (the viewer's Print, or auto-print). */
export async function markReportPrinted(facilityId: number, reportDocumentId: number, userId: number) {
  if (!facilityId || !reportDocumentId) return;
  // MarkPrinted records who printed it (the token's user; ?userId= is the fallback).
  await markReport(API.reportMarkPrinted(facilityId, reportDocumentId), { facilityId, userId, reportDocumentId, to: 'printed', saved: false }, { userId });
}

/**
 * Stand-in client for a tree with no QueryClientProvider (isolated component
 * tests of the sidebar). The query is disabled there, so it never fetches.
 */
const detachedClient = new QueryClient();

async function fetchReadyReportCount(facilityId: number, userId: number): Promise<number> {
  const { data } = await apiClient.get<number>(API.reportReadyCount(facilityId), { params: { userId } });
  return typeof data === 'number' ? data : Number(data) || 0;
}

/**
 * The signed-in user's ready-report count for the current facility, or 0
 * while it is unknown. Polls once a minute and whenever the window regains
 * focus; the Reports page and the open/print paths push changes in between.
 */
export function useReadyReportCount(enabled = true): number {
  const facilityId = useGlobalStore((s) => s.facilityId);
  const userId = useGlobalStore((s) => s.userId);
  const contextClient = useContext(QueryClientContext);
  const queryClient = contextClient ?? detachedClient;
  const on = enabled && !!contextClient && facilityId > 0 && userId > 0;

  const { data } = useQuery({
    queryKey: readyReportsKey(facilityId, userId),
    queryFn: () => fetchReadyReportCount(facilityId, userId),
    enabled: on,
    refetchInterval: READY_REPORT_POLL_MS,
    refetchOnWindowFocus: true,
    staleTime: 15_000,
    // A badge is not worth retries or an error state: show nothing until it answers.
    retry: false,
  }, queryClient);

  useEffect(() => {
    if (!on) return;
    const onChanged = (e: Event) => {
      const detail = (e as CustomEvent<{ facilityId: number; userId: number; count?: number }>).detail;
      if (!detail || detail.facilityId !== facilityId || detail.userId !== userId) return;
      const key = readyReportsKey(facilityId, userId);
      if (typeof detail.count === 'number') queryClient.setQueryData(key, detail.count);
      else void queryClient.invalidateQueries({ queryKey: key });
    };
    window.addEventListener(CHANGED_EVENT, onChanged);
    return () => window.removeEventListener(CHANGED_EVENT, onChanged);
  }, [on, facilityId, userId, queryClient]);

  return on ? data ?? 0 : 0;
}
